import React, { useState, useEffect, useRef } from "react";
import SectionHeader from "../../components/sectionHeader/sectionHeader";
import Input from "../../components/inputs/input/input";
import "./perfilUsuario.scss";
import { getApiInstance } from "../../utils/api";
import Button from "../../components/button/button";
import Notification from "../../components/notification/notification";
import Switch from "../../components/switch/switch";
import { FiDownload } from "react-icons/fi";
import { mensajeErrorAuth, validarPassword } from "../../utils/authUtils";


export default function PerfilUsuario({ codUsuario }) {
    const api = getApiInstance();
    const notificationRef = useRef(null);
    const [perfil, setPerfil] = useState({
        nombre: "",
        apellidos: "",
        email: "",
        password: "",
        passwordActual: "",
        codUsuario: "",
        notificacionEmail: false,
        notificacionesSMS: false,
        notificacionesWeb: true,
    });

    useEffect(() => {
        fetchUsuario();
    }, []);

    const fetchUsuario = async () => {
     try {
            const [resUser, resEmpresa] = await Promise.all([
                api.get("/api/Usuario/GetCurrentUser"),
                api.get("/api/Usuario/GetCurrentUserWithEmpresa"),
            ]);

            setPerfil({
                nombre: resUser.data.nombre,
                apellidos: resUser.data.apellidos,
                email: resUser.data.email,
                password: "",
                passwordActual: "",
                codUsuario: resUser.data.codUsuario,
                notificacionEmail: resUser.data.notificacionEmail ?? false,
                notificacionesSMS: resUser.data.notificacionesSMS ?? false,
                notificacionesWeb: resUser.data.notificacionesWeb ?? true,
                codTipoEmpresa: resEmpresa.data.codTipoEmpresa,
                codEmpresa: resUser.data.codEmpresa,
            });
        } catch (error) {
            showNotification("Error al obtener datos del usuario", "error");
        }
    };

    const descargarManual = async () => {
        try {
            const response = await api.get("/api/Mueble/DownloadManualUsuario", {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const contentDisposition = response.headers["content-disposition"];
            let fileName = "manual_usuario.pdf";

            if (contentDisposition) {
            const match = contentDisposition.match(/filename="?(.+)"?/);
            if (match?.[1]) fileName = match[1];
            }

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            showNotification("Error al descargar el manual", "error");
        }
    };


    const showNotification = (message, type) => {
        notificationRef.current.addNotification(message, type);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPerfil((prev) => ({ ...prev, [name]: value }));
    };

    const handleToggleNotificacion = async (tipo, label) => {
        const campo = `notificacion${tipo}`;
        const nuevoEstado = !perfil[campo];

        try {
            // Actualización optimista
            setPerfil((prev) => ({ ...prev, [campo]: nuevoEstado }));

            // Enviar al servidor: usar query params en lugar de body
            const url = `/api/Notificaciones/Toggle${tipo}Notificaciones?codUsuario=${encodeURIComponent(
                perfil.codUsuario
            )}&activar=${encodeURIComponent(nuevoEstado)}`;

            await api.post(url);

            // Re-sincronizar con el servidor para asegurar que el estado persiste
            await fetchUsuario();

            showNotification(
                `${label} ${nuevoEstado ? "activadas" : "desactivadas"}`,
                "success"
            );
        } catch (error) {
            // Revertir en caso de error
            setPerfil((prev) => ({ ...prev, [campo]: !nuevoEstado }));
            showNotification(
                `Error al cambiar ${label.toLowerCase()}`,
                "error"
            );
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Cambiar la contraseña pide la actual (el API la comprueba).
        if (perfil.password) {
            const errorPassword = validarPassword(perfil.password);
            if (errorPassword) {
                showNotification(errorPassword, "warning");
                return;
            }
            if (!perfil.passwordActual) {
                showNotification(
                    "Introduce tu contraseña actual para cambiarla.",
                    "warning"
                );
                return;
            }
        }

        try {
            const payload = {
                codUsuario: perfil.codUsuario,
                nombre: perfil.nombre,
                apellidos: perfil.apellidos,
                password: perfil.password || undefined,
                passwordActual: perfil.password ? perfil.passwordActual : undefined,
                notificacionEmail: perfil.notificacionEmail, 
            };

            await api.put("/api/Usuario/ActualizarPerfil", payload);

            showNotification(
                perfil.password
                    ? "Perfil y contraseña actualizados correctamente"
                    : "Perfil actualizado correctamente",
                "success"
            );
            fetchUsuario();
        } catch (error) {
            showNotification(
                mensajeErrorAuth(error, "Error al guardar cambios"),
                "error"
            );
        }
    };

    const getInitials = (nombre, apellidos) => {
        if (!nombre || !apellidos) return "";
        return `${nombre.charAt(0).toUpperCase()}${apellidos
            .charAt(0)
            .toUpperCase()}`;
    };

    return (
        <form className="perfil-usuario" onSubmit={handleSubmit}>
            <Notification ref={notificationRef} />
            <SectionHeader
                title="Mi cuenta"
                subtitle="Gestiona tu perfil de usuario"
            />

            <div className="perfil-usuario__header">
                <div className="avatar-container">
                    <div className="avatar">
                        {getInitials(perfil.nombre, perfil.apellidos)}
                    </div>
                </div>

                <div className="user-info">
                    <h1>
                        {perfil.nombre} {perfil.apellidos}
                    </h1>
                    <p className="email">{perfil.email}</p>
                </div>
            </div>

            <div className="perfil-usuario__content">
                <div className="form-section">
                    <h2>Manual de usuario</h2>
                    <Button
                        label={
                            <span>
                                <FiDownload style={{ marginRight: "8px" }} />
                                Descargar manual de usuario
                            </span>
                    }
                        variant="outlined"
                        onClick={descargarManual}
                        type="button"
                    />
                </div>

                <div className="form-section">
                    <h2>Información personal</h2>
                    <Input
                        label="Nombre"
                        name="nombre"
                        value={perfil.nombre}
                        onChange={handleChange}
                    />
                    <Input
                        label="Apellidos"
                        name="apellidos"
                        value={perfil.apellidos}
                        onChange={handleChange}
                    />
                    <Input
                        type="email"
                        label="Email"
                        name="email"
                        value={perfil.email}
                        disabled
                        required
                    />
                    <p className="form-section__input-message">
                        * Para cambiar tu email, contacta con un administrador.
                    </p>
                </div>

                <div className="form-section">
                    <h2>Contraseña</h2>
                    <Input
                        type="password"
                        label="Contraseña actual"
                        name="passwordActual"
                        placeholder="Solo si vas a cambiarla"
                        value={perfil.passwordActual}
                        autoComplete="current-password"
                        onChange={handleChange}
                    />
                    <Input
                        type="password"
                        label="Nueva contraseña"
                        name="password"
                        placeholder="Mínimo 8 caracteres"
                        value={perfil.password}
                        autoComplete="new-password"
                        onChange={handleChange}
                    />
                </div>

                                               <div className="notifications-section">
                    <h2>Preferencias de notificación</h2>
                    <div className="switch-group">
                        <Switch
                            isOn={perfil.notificacionEmail}
                            onChange={() =>
                                handleToggleNotificacion(
                                    "Email",
                                    "Notificaciones por email"
                                )
                            }
                            label="Recibir notificaciones por email"
                        />
                        <Switch
                            isOn={true}      // siempre activado
                            disabled         // no se puede desactivar
                            label="Notificaciones en la web"
                        />
                    </div>
                </div>

                <div className="actions">
                    <Button
                        label="Guardar cambios"
                        variant="filled"
                        type="submit"
                    />
                </div>
            </div>
        </form>
    );
}
